// ***************************************************************************************
// Local persistence.
//
// Two separate things are stored, and conflating them would lose results:
//
//   ATTEMPT  the game in progress. Replaced on every committed move, undo and restart.
//   BEST     the best COMPLETED score for a puzzle. Restart must never touch this.
//
// The attempt is stored as its MOVE LIST, not as a board. A board could be saved
// mid-animation or mid-settle and restore into a state play could not have reached; a
// move list replays into a consistent board or fails cleanly. See game.ts.
//
// Everything read back is validated. localStorage is shared with anything else on the
// origin, survives across versions, and can be edited by hand.
// ***************************************************************************************

import type { TileRef } from '../engine/modes.ts';

// v2: the log gained shuffles. A v1 save is a list of words with no way to say where a
// shuffle went, so it cannot be upgraded - the key changes and old saves are ignored
// rather than replayed into the wrong board.
const ATTEMPT_KEY = 'wordsoup.attempt.v2';
const BEST_KEY = 'wordsoup.best.v1';
const PLAYER_KEY = 'wordsoup.player.v1';
const NAME_KEY = 'wordsoup.name.v1';
const STREAK_KEY = 'wordsoup.streak.v1';

export interface StoredAttempt {
  puzzleId: string;
  modeId: string;
  rulesVersion: number;
  dictionaryVersion: string;
  /**
   * The action log. A word is its list of tiles; a shuffle is `{ shuffle: true }`.
   * Stored rather than the board, so a save written mid-animation cannot restore into a
   * state play could not have reached.
   */
  actions: StoredAction[];
  status: 'playing' | 'finished';
  finishReason: string | null;
  /** Kept for display only. The authority is the replayed move list. */
  score: number;
  savedAt: number;
}

export type StoredAction = { x: number; y: number }[] | { shuffle: true };

export interface StoredBest {
  puzzleId: string;
  score: number;
  words: number;
  longestWordLength: number;
  cleared: boolean;
  tilesRemaining: number;
  /** True once the server has confirmed it. Until then the UI says "best on this device". */
  confirmed: boolean;
  savedAt: number;
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);

    if (!raw) return null;

    return JSON.parse(raw) as T;
  } catch {
    // A private window, cleared site data, or a browser that throws on access. Not an
    // error worth showing anybody - the game just starts fresh.
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or a blocked store. Play continues; the attempt just will not survive a
    // reload, which is better than an error the player can do nothing about.
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* see write() */
  }
}

function isShuffle(value: unknown): value is { shuffle: true } {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as { shuffle?: unknown }).shuffle === true
  );
}

function isTileList(value: unknown): value is TileRef[] {
  if (!Array.isArray(value)) return false;

  return value.every(
    (tile) =>
      typeof tile === 'object' &&
      tile !== null &&
      Number.isInteger((tile as TileRef).x) &&
      Number.isInteger((tile as TileRef).y),
  );
}

/** Validated on the way out, so a corrupt or hand-edited entry is simply ignored. */
export function loadAttempt(): StoredAttempt | null {
  const stored = read<StoredAttempt>(ATTEMPT_KEY);

  if (!stored) return null;

  if (typeof stored.puzzleId !== 'string' || stored.puzzleId.length === 0) return null;
  if (typeof stored.modeId !== 'string') return null;
  if (!Number.isInteger(stored.rulesVersion)) return null;
  if (!Array.isArray(stored.actions)) return null;
  if (!stored.actions.every((entry) => isShuffle(entry) || isTileList(entry))) return null;
  if (stored.status !== 'playing' && stored.status !== 'finished') return null;

  return stored;
}

export function saveAttempt(attempt: StoredAttempt): void {
  write(ATTEMPT_KEY, attempt);
}

export function clearAttempt(): void {
  remove(ATTEMPT_KEY);
}

export function loadBest(puzzleId: string): StoredBest | null {
  const all = read<Record<string, StoredBest>>(BEST_KEY);

  if (!all || typeof all !== 'object') return null;

  const best = all[puzzleId];

  if (!best || !Number.isFinite(best.score)) return null;

  return best;
}

/**
 * Record a completed attempt if it beats what is stored.
 *
 * Returns whether it was an improvement, which is what the finish screen reports. A
 * restart cannot reach this - only a finished game does.
 */
export function recordBest(best: StoredBest): boolean {
  const all = read<Record<string, StoredBest>>(BEST_KEY) ?? {};
  const existing = all[best.puzzleId];

  if (existing && existing.score >= best.score) {
    // Not an improvement, but a later server confirmation of the same score still is.
    if (best.confirmed && !existing.confirmed) {
      all[best.puzzleId] = { ...existing, confirmed: true };
      write(BEST_KEY, all);
    }

    return false;
  }

  all[best.puzzleId] = best;

  // Keep the store from growing without limit. A year of dailies is plenty of history for
  // something that is only ever read one puzzle at a time.
  const keys = Object.keys(all);

  if (keys.length > 400) {
    const oldest = keys.sort((a, b) => (all[a]!.savedAt ?? 0) - (all[b]!.savedAt ?? 0));

    for (const key of oldest.slice(0, keys.length - 400)) delete all[key];
  }

  write(BEST_KEY, all);

  return true;
}

export function markBestConfirmed(puzzleId: string): void {
  const all = read<Record<string, StoredBest>>(BEST_KEY);

  if (!all) return;

  const existing = all[puzzleId];

  if (!existing) return;

  all[puzzleId] = { ...existing, confirmed: true };

  write(BEST_KEY, all);
}


// ***************************************************************************************
// The streak: how many days in a row you have finished a puzzle.
//
// PER SITE, and that is not a bug to fix later - each mode is its own origin, so its own
// localStorage, and there is no shared store to put this in without an account. A Hunt
// streak and a Ladder streak are separate things, which is arguably the honest answer
// anyway: they are different games.
//
// The rules live in `advanceStreak`, pure and tested, because every one of them is a
// judgement that is easy to state and easy to get wrong - and getting it wrong means
// wiping a streak somebody built up, which is the one thing a streak must never do by
// accident.
// ***************************************************************************************
export interface StoredStreak {
  /** The day number of the last puzzle finished. 0 when nothing has been. */
  lastDay: number;
  current: number;
  best: number;
}

const NO_STREAK: StoredStreak = { lastDay: 0, current: 0, best: 0 };

/**
 * The streak after finishing the puzzle for `day`.
 *
 * Pure. The cases, and why:
 *
 *   same day again      unchanged. Replaying today does not extend anything, and must
 *                       not - restarting a puzzle is free and encouraged.
 *   the next day        the streak grows. This is the whole mechanic.
 *   a day was missed    back to 1. Not to 0: today still counts, and telling somebody
 *                       their streak is zero on a day they played is simply wrong.
 *   an OLDER puzzle     unchanged. Following a shared link to last Tuesday's puzzle is
 *                       not a day of play, and it certainly should not break a live
 *                       streak by rewriting lastDay backwards.
 */
export function advanceStreak(stored: StoredStreak, day: number): StoredStreak {
  if (!Number.isInteger(day) || day <= 0) return stored;

  if (day <= stored.lastDay) return stored;

  const current = day === stored.lastDay + 1 ? stored.current + 1 : 1;

  return {
    lastDay: day,
    current,
    best: Math.max(stored.best, current),
  };
}

/**
 * The streak as it stands TODAY, for display.
 *
 * A stored streak is history; whether it is still alive depends on what day it is now. A
 * run that ended on Monday is not a streak on Wednesday, and showing the old number would
 * be a lie the player only discovers when it resets.
 */
export function liveStreak(stored: StoredStreak, today: number): number {
  if (stored.current === 0) return 0;

  return today - stored.lastDay <= 1 ? stored.current : 0;
}

export function loadStreak(): StoredStreak {
  const stored = read<StoredStreak>(STREAK_KEY);

  if (
    !stored ||
    !Number.isInteger(stored.lastDay) ||
    !Number.isInteger(stored.current) ||
    !Number.isInteger(stored.best) ||
    stored.current < 0 ||
    stored.best < 0
  ) {
    return NO_STREAK;
  }

  return stored;
}

/** Record a finished puzzle and return the streak it leaves. */
export function recordStreak(day: number): StoredStreak {
  const next = advanceStreak(loadStreak(), day);

  write(STREAK_KEY, next);

  return next;
}

// ***************************************************************************************
// Anonymous identity.
//
// A random id in this browser's storage. Not an account, not a fingerprint, and it cannot
// follow anybody to another device - which the UI says plainly rather than implying a
// profile exists.
// ***************************************************************************************
export function playerId(): string {
  const existing = read<string>(PLAYER_KEY);

  if (typeof existing === 'string' && existing.length >= 8) return existing;

  const bytes = new Uint8Array(16);

  crypto.getRandomValues(bytes);

  const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

  write(PLAYER_KEY, id);

  return id;
}

export function playerName(): string | null {
  const name = read<string>(NAME_KEY);

  return typeof name === 'string' && name.length > 0 ? name : null;
}

export function savePlayerName(name: string): void {
  const trimmed = name.trim().slice(0, 20);

  if (trimmed.length === 0) remove(NAME_KEY);
  else write(NAME_KEY, trimmed);
}

/**
 * A name arriving in the URL, as `?name=`.
 *
 * THE HUB CANNOT WRITE THIS DIRECTLY. Each version is its own origin, so its own
 * localStorage, and a name typed on the hub page is invisible to every game. Carrying it
 * on the link is the way across that boundary without accounts or a shared cookie domain.
 *
 * Returns the name adopted, or null. The caller is expected to strip the parameter from
 * the address bar afterwards: a link that is shared onwards should not rename whoever
 * follows it.
 */
export function nameFromUrl(search: string): string | null {
  let value: string | null = null;

  try {
    value = new URLSearchParams(search).get('name');
  } catch {
    return null;
  }

  if (value === null) return null;

  const trimmed = value.trim().slice(0, 20);

  return trimmed.length > 0 ? trimmed : null;
}
